import debounce from "lodash.debounce";

import type {
    V1GuideToApp,
    V1GuideToWeb,
    V1ShellToWeb,
} from "@khanacademy/embedded-webapp-schema";
import {listenForSignal, sendSignal} from "@khan/integrations";
import {Errors, KAError} from "@khan/logging";

import type {OpenedState, UXStackMessenger} from "./types.ts";

const DEBOUNCE_DELAY_MS = 50;

export const guideMessenger: UXStackMessenger = Object.freeze({
    listenForCloseDismissable(handler) {
        return listenForSignal<V1GuideToWeb.CloseDismissibleUX>(
            "guide:closeDismissibleUX",
            handler,
        );
    },
    sendStackUpdate(breadcrumbs) {
        sendSignal({
            type: "guide:uxopenedclosed",
            data: {breadcrumbs},
        });
    },
});

export const createBreadcrumb = (
    slug: string,
    behavior: V1GuideToApp.DismissBehavior,
    state?: OpenedState,
): V1GuideToApp.Breadcrumb => {
    const breadcrumb: V1GuideToApp.Breadcrumb = {
        slug,
        behavior,
    };
    if (behavior === "confirm-or-cancel") {
        breadcrumb.canConfirm = state?.canConfirm ?? true;
    }
    if (state?.translatedActionLabels != null) {
        breadcrumb.translatedActionLabels = state.translatedActionLabels;
    }
    if (state?.translatedTitle != null) {
        breadcrumb.translatedTitle = state.translatedTitle;
    }
    return breadcrumb;
};

/**
 * Manage the UX context stack so that the mobile app go move "back".
 */
export class UXStack<Breadcrumb extends V1GuideToApp.Breadcrumb> {
    private readonly _listeners = new Map<
        string,
        (action: V1GuideToWeb.DismissAction) => void
    >();
    private readonly _stack: Array<Breadcrumb> = [];
    private readonly _unsubscribe: Array<() => void>;
    private readonly _signalMobileApp: any;

    constructor(messenger: UXStackMessenger = guideMessenger) {
        // Listen for the mobile app signaling that it wants us to close
        // some or all of the UX in the stack.
        this._unsubscribe = [
            messenger.listenForCloseDismissable((data) =>
                this._closeHandler(data),
            ),
        ];
        if (messenger.listenForCloseDismissableV2) {
            messenger.listenForCloseDismissableV2((data) =>
                this._closeV2(data),
            );
        }

        this._signalMobileApp = debounce(() => {
            messenger.sendStackUpdate(this._stack);
        }, DEBOUNCE_DELAY_MS);
    }

    /**
     * Indicate that a UX context is active in a given state.
     *
     * This is used to push a UX context onto the top of the stack and inform
     * the mobile app accordingly. If the given slug is already in the stack,
     * it will be moved to the top of the stack and updated.
     *
     * NOTE: The thing that handles closing an item when the app requests it may
     * not be the same thing that adds it to the stack via the `opened`
     * call. Anything that is "open" (i.e. in the stack) must have a something
     * listening to the close event when the event arrives or there will be an
     * error. This separation is provided for convenience of use but it does
     * add a burden of ensuring that the close listener is registered before any
     * related close signal is received from the mobile app.
     *
     * @param slug A descriptive unique slug identifying the new active context.
     * @param behavior The behavior for dismissing the new active UX
     * context.
     * @param [state] Optional state that the app integrating webapp can use
     * to customize the experience.
     * @throws if no close listener is registered for the given slug. Any
     * UX participating in the stack must register for the close signal.
     */
    opened(breadcrumb: Breadcrumb): void {
        // If the slug is already in the stack, we need to remove that.
        const existing = this._stack.findIndex(
            (bc) => bc.slug === breadcrumb.slug,
        );
        if (existing >= 0) {
            this._stack.splice(existing, 1);
        }
        this._stack.push(breadcrumb);
        this._signalMobileApp();
    }

    /**
     * Indicate that the given UX context has closed.
     *
     * This is used to remove a context from the stack. The slug is
     * used to identify which UX context to remove. If UX is working as
     * intended, this should be the most recently added context, but we
     * cope with unordered closing by using the slug.
     *
     * If the slug is not found in the stack, this is a no-op.
     *
     * @param slug A descriptive unique slug identifying the UX context
     * that has closed.
     */
    closed(slug: string): void {
        const index = this._stack.findLastIndex((item) => item.slug === slug);
        if (index >= 0) {
            this._stack.splice(index, 1);
            this._signalMobileApp();
        }
    }

    /**
     * Listen for the close signal for a given UX context slug.
     *
     * NOTE: The thing that handles closing an item when the app requests it may
     * not be the same thing that adds it to the stack via the `opened`
     * call. Anything that is "open" (i.e. in the stack) must have a something
     * listening to the close event when the event arrives or there will be an
     * error. This separation is provided for convenience of use but it does
     * add a burden of ensuring that the close listener is registered before any
     * related close signal is received from the mobile app.
     *
     * @param slug The slug of the UX context to listen for.
     * @param cb The callback to invoke when the signal is received.
     * @returns A function that can be used to unsubscribe from the signal.
     * This is useful for React components that need to resubscribe due to a
     * callback change, for example. Be aware that if you unsubscribe from the
     * signal for a slug, but then the slug is present in the stack when
     * the mobile requests it be closed, it will result in an error.
     * @throws if a close listener is already registered for the given slug.
     */
    listenForClose(
        slug: string,
        cb: (action: V1GuideToWeb.DismissAction) => void,
    ): () => void {
        if (this._listeners.has(slug)) {
            throw new KAError(
                `A listener for the given UX context is already registered`,
                Errors.NotAllowed,
                {
                    sentryData: {
                        contexts: {
                            uxSlug: {slug},
                        },
                    },
                },
            );
        }
        this._listeners.set(slug, cb);
        let unsubscribed = false;
        return () => {
            if (unsubscribed) {
                return;
            }
            unsubscribed = true;
            this._listeners.delete(slug);
        };
    }

    /**
     * Signal that the given UX context should be closed.
     *
     * @param slug The slug of the UX context to close.
     * @param action The action to take when closing the UX context.
     */
    private _closeSlug(slug: string, action: V1GuideToWeb.DismissAction): void {
        const listener = this._listeners.get(slug);
        if (!this._listeners.has(slug)) {
            // We don't have a listener. That's a bad situation. Folks MUST
            // listen for the close signal if they are participating in the
            // stack or we can never pop something off the stack.
            throw new KAError(
                `No listener for closing given UX context; the UX context is participating in the stack, so something must listen for the close signal`,
                Errors.InvalidInput,
                {
                    sentryData: {
                        contexts: {
                            uxSlug: {slug},
                        },
                    },
                },
            );
        }
        // NOTE: We should ALWAYS have a listener but we don't know if callers
        // closed a context properly and stopped listening while we were
        // asking all contexts to close, so we don't error here. Instead,
        // we make sure that when `opened` is called above, that a listener has
        // already been registered.
        listener?.(action);
    }

    /**
     * Signal that the topmost UX context should be closed.
     */
    private _closeTop(action: V1GuideToWeb.DismissAction): void {
        // We get the most recently added slug and then invoke the listener
        // for that slug to close.
        const top = this._stack[this._stack.length - 1];
        if (top != null) {
            this._closeSlug(top.slug, action);
        }
    }

    /**
     * Handler for the mobile app requesting some or all of the stack be closed.
     *
     * This handles the mobile app signal to close the UX at the top of the
     * stack or all UX in the stack.
     *
     * @param data The data from the `V1GuideToWeb.CloseDismissibleUX` mobile
     * app signal.
     */
    private _closeHandler({
        dismissAction,
        depth,
    }: V1GuideToWeb.CloseDismissibleUX["data"]): void {
        switch (depth) {
            // If depth is top, get the slug of the topmost item and signal the
            // appropriate handler to close (that should then lead to the `closed`
            // call above which ultimately pops it off the stack).
            case "top":
                this._closeTop(dismissAction);
                break;

            // If depth is all, just tell everything to close - could either loop
            // stack or just tell everyone and let them handle it. They should each
            // then signal the closed action above, removing them from the stack.
            case "all":
                // We tell the topmost UX to close. That should eventually
                // trigger at least one `closed()` call for that slug, but
                // could feasibly close others as well. However, our listeners
                // may do the closing in some async manner, so we have to just
                // loop and tell them all to close.
                // That said, we can't trust this._stack as it will mutate, so
                // we must get a copy of the slugs BEFORE modifying.
                const openSlugs = this._stack.map((item) => item.slug);
                for (const slug of openSlugs) {
                    this._closeSlug(slug, dismissAction);
                }
                break;

            default:
                throw new KAError(
                    "Unexpected guide UX close depth",
                    Errors.InvalidInput,
                    {
                        sentryData: {
                            contexts: {
                                eventData: {
                                    dismissAction,
                                    depth,
                                },
                            },
                        },
                    },
                );
        }
    }

    /**
     * Handler for "v2" of the signal, where the mobile app can request that
     * a specific item be removed, by slug.
     */
    private _closeV2(data: V1ShellToWeb.CloseDismissibleUXv2["data"]): void {
        if ("depth" in data) {
            this._closeHandler(data);
        } else {
            this._closeSlug(data.slug, data.dismissAction);
        }
    }

    /**
     * Tear down the UX stack, including all listeners.
     *
     * Providing this so that we can tidy-up between tests. This will
     * unsubscribe from the mobile app signal, clear the stack,
     * and clear all listeners, making this instance inert and usable.
     *
     * In non-test scenarios, things should be properly cleaning up, or there's
     * a full-page navigation and we don't care. For dev, we throw an error when
     * this is called to let folks know they shouldn't call it. In prod, it's a
     * no-op.
     */
    tearDown(): void {
        if (process.env.NODE_ENV === "test") {
            this._signalMobileApp.cancel();
            this._unsubscribe.forEach((f) => f());
            this._listeners.clear();
            this._stack.length = 0;
        } else if (process.env.NODE_ENV !== "production") {
            throw new Error("clear() should only be called in tests");
        }
    }
}
